/**
 * Tiny fetch wrapper used by every TanStack Query hook.
 *
 *   - `fetchJson<T>(path, init?)` — GET (or any method via init) → parsed
 *     JSON or throws ApiError.
 *   - `postJson<T>(path, body)` — POST application/json. Used for the auth
 *     endpoints (login, setup, change-password, etc.).
 *   - `postForm<T>(path, fields)` — POST multipart/form-data. Used for
 *     `/api/entries` since the backend uses `Form(...)` parameters.
 *
 * On every state-changing request we attach the CSRF header from the
 * `wsl_csrf` cookie set by the backend on login.
 *
 * On a 401 from any endpoint we redirect the browser to `/login?next=...`
 * (unless we are already on /login or /auth/setup). This is the simplest
 * cross-cutting auth gate and mirrors the auth/dependencies behavior on
 * the backend, which 401s an unauthenticated /api request.
 *
 * `ApiError` carries:
 *   - `status` (HTTP status)
 *   - `message` (best-effort human-readable)
 *   - `rawDetail` (original FastAPI `detail`, including object payloads)
 *   - `errors` (per-field errors when the backend returns the
 *     `{ ok: false, errors: { field: msg } }` envelope)
 */

const CSRF_COOKIE = "wsl_csrf";
const CSRF_HEADER = "X-CSRF-Token";
const PUBLIC_PATHS = ["/login", "/auth/setup"];

export class ApiError extends Error {
  status: number;
  errors?: Record<string, string>;
  detail?: string;
  rawDetail?: unknown;

  constructor(opts: {
    status: number;
    message: string;
    errors?: Record<string, string>;
    detail?: string;
    rawDetail?: unknown;
  }) {
    super(opts.message);
    this.name = "ApiError";
    this.status = opts.status;
    this.errors = opts.errors;
    this.detail = opts.detail;
    this.rawDetail = opts.rawDetail;
  }
}

function readCookie(name: string): string | undefined {
  if (typeof document === "undefined") return undefined;
  const match = document.cookie
    .split("; ")
    .find((row) => row.startsWith(name + "="));
  return match ? decodeURIComponent(match.slice(name.length + 1)) : undefined;
}

function isPublicRoute(): boolean {
  if (typeof window === "undefined") return false;
  return PUBLIC_PATHS.some((p) => window.location.pathname.startsWith(p));
}

function redirectToLogin(): void {
  if (typeof window === "undefined") return;
  if (isPublicRoute()) return;
  const next = encodeURIComponent(
    window.location.pathname + window.location.search,
  );
  window.location.assign(`/login?next=${next}`);
}

async function parseJsonOrEmpty(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch {
    return { _raw: text };
  }
}

function asErrorPayload(payload: unknown): {
  errors?: Record<string, string>;
  detail?: string;
  rawDetail?: unknown;
} {
  if (!payload || typeof payload !== "object") return {};
  const obj = payload as Record<string, unknown>;
  const result: {
    errors?: Record<string, string>;
    detail?: string;
    rawDetail?: unknown;
  } = {};
  if (obj.errors && typeof obj.errors === "object") {
    result.errors = obj.errors as Record<string, string>;
  }
  if ("detail" in obj) {
    result.rawDetail = obj.detail;
  }
  if (typeof obj.detail === "string") {
    result.detail = obj.detail;
  } else if (obj.detail && typeof obj.detail === "object") {
    // FastAPI sometimes nests {ok:false, errors:{...}} inside `detail`.
    const nested = obj.detail as Record<string, unknown>;
    if (nested.errors && typeof nested.errors === "object") {
      result.errors = nested.errors as Record<string, string>;
    }
    if (typeof nested.message === "string") {
      result.detail = nested.message;
    }
  }
  return result;
}

function buildHeaders(
  init: RequestInit | undefined,
  method: string,
  contentType?: string,
): Headers {
  const headers = new Headers(init?.headers);
  headers.set("Accept", "application/json");
  if (contentType) headers.set("Content-Type", contentType);
  // CSRF: backend's require_csrf checks GET-shaped methods are exempt.
  // We attach the header on every state-changing method.
  if (!["GET", "HEAD", "OPTIONS"].includes(method.toUpperCase())) {
    const csrf = readCookie(CSRF_COOKIE);
    if (csrf) headers.set(CSRF_HEADER, csrf);
  }
  return headers;
}

async function handle<T>(
  path: string,
  response: Response,
): Promise<T> {
  const payload = await parseJsonOrEmpty(response);
  if (response.status === 401) {
    redirectToLogin();
  }
  if (!response.ok) {
    const { errors, detail, rawDetail } = asErrorPayload(payload);
    throw new ApiError({
      status: response.status,
      message: detail ?? `Request to ${path} failed (${response.status})`,
      errors,
      detail,
      rawDetail,
    });
  }
  return payload as T;
}

export async function fetchJson<T>(path: string, init?: RequestInit): Promise<T> {
  const method = (init?.method ?? "GET").toUpperCase();
  const response = await fetch(path, {
    ...init,
    headers: buildHeaders(init, method),
    credentials: "same-origin",
  });
  return handle<T>(path, response);
}

export async function postJson<T>(path: string, body: unknown): Promise<T> {
  const response = await fetch(path, {
    method: "POST",
    headers: buildHeaders(undefined, "POST", "application/json"),
    body: JSON.stringify(body ?? {}),
    credentials: "same-origin",
  });
  return handle<T>(path, response);
}

export async function postForm<T>(
  path: string,
  fields: Record<string, string | undefined>,
): Promise<T> {
  const formData = new FormData();
  for (const [key, value] of Object.entries(fields)) {
    if (value !== undefined) formData.append(key, value);
  }
  const response = await fetch(path, {
    method: "POST",
    headers: buildHeaders(undefined, "POST"),
    body: formData,
    credentials: "same-origin",
  });
  return handle<T>(path, response);
}

export async function postMultipart<T>(
  path: string,
  formData: FormData,
): Promise<T> {
  const response = await fetch(path, {
    method: "POST",
    headers: buildHeaders(undefined, "POST"),
    body: formData,
    credentials: "same-origin",
  });
  return handle<T>(path, response);
}
